import config from "appConfig";

const theory = `<p>Ambisonics is a technology for spatial sound recording, mixing, and playback first developed in the 1970s [<span class=cmbx-10>gerzon1973</span>, <span class=cmbx-10>fellgett1975</span>, <span class=cmbx-10>gerzon1975</span>] with support by the British National Research Development Corporation. In contrast to other surround sound reproduction formats, the Ambisonic audio format conveys a representation of the full, three-dimensional audio scene instead of information about individual sound sources (as in object-based formats) or loudspeaker channels (as in channel-based formats). Ambisonic audio streams can be reproduced using various reproduction layouts such as surround loudspeaker setups or headphones, providing flexibility for different applications.<br class=newline>
<h4 class=likesubsectionHead>Spherical harmonics</h4>
<p>
   Ambisonics is based on the use of so-called Spherical Harmonics
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msubsup>
   </math>
   of order
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   and degree
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>m</mi>
   </math>
   , with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>0</mn>
      <mo class=MathClass-rel stretchy=false>≤</mo>
      <mi>n</mi>
      <mo class=MathClass-rel stretchy=false>≤</mo>
      <mi>N</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>m</mi>
      <mo class=MathClass-rel stretchy=false>=</mo>
      <mo class=MathClass-bin stretchy=false>−</mo>
      <mi>n</mi>
      <mo class=MathClass-rel stretchy=false>≥</mo>
      <mi>m</mi>
      <mo class=MathClass-rel stretchy=false>≥</mo>
      <mi>n</mi>
   </math>
   , which provide a solution for the <span class=cmti-10>Helmhotz Equation</span> [<span class=cmbx-10>ahrens2012</span>] given in terms of spherical coordinates (as introduced in Lesson 1.15).<br class=newline>
<p>In acoustics, the real-valued SHs are typically used which can be defined as [<span class=cmbx-10>nachbar2011</span>]
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msubsup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc stretchy=false>,</mo>
            <mi>φ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel stretchy=false>=</mo>
            <msubsup>
               <mrow>
                  <mi>N</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-rel stretchy=false>|</mo>
                  <mi>m</mi>
                  <mo class=MathClass-rel stretchy=false>|</mo>
               </mrow>
            </msubsup>
            <mi> </mi>
            <msubsup>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-rel stretchy=false>|</mo>
                  <mi>m</mi>
                  <mo class=MathClass-rel stretchy=false>|</mo>
               </mrow>
            </msubsup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi class=qopname>sin</mi>
            <mo>⁡</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mrow class=cases>
               <mrow>
                  <mo fence=true form=prefix>{</mo>
                  <mrow>
                     <mtable align=axis style="" equalrows=false columnlines=none equalcolumns=false class=array displaystyle=true>
                        <mtr class=array-row>
                           <mtd class=array-td columnalign=left>
                              <mi class=qopname>sin</mi>
                              <mo>⁡</mo>
                              <mo class=MathClass-rel stretchy=false>|</mo>
                              <mi>m</mi>
                              <mo class=MathClass-rel stretchy=false>|</mo>
                              <mi>φ</mi>
                           </mtd>
                           <mtd class=array-td columnalign=left>
                              <mstyle class=text>
                                 <mtext>for </mtext>
                                 <mstyle class=math>
                                    <mi>m</mi>
                                    <mo class=MathClass-rel stretchy=false><</mo>
                                    <mn>0</mn>
                                 </mstyle>
                              </mstyle>
                           </mtd>
                        </mtr>
                        <mtr class=array-row>
                           <mtd class=array-td columnalign=left>
                              <mi class=qopname>cos</mi>
                              <mo>⁡</mo>
                              <mo class=MathClass-rel stretchy=false>|</mo>
                              <mi>m</mi>
                              <mo class=MathClass-rel stretchy=false>|</mo>
                              <mi>φ</mi>
                           </mtd>
                           <mtd class=array-td columnalign=left>
                              <mstyle class=text>
                                 <mtext>for </mtext>
                                 <mstyle class=math>
                                    <mi>m</mi>
                                    <mo class=MathClass-rel stretchy=false>≥</mo>
                                    <mn>0</mn>
                                 </mstyle>
                              </mstyle>
                           </mtd>
                        </mtr>
                        <mtr class=array-row>
                           <mtd class=array-td columnalign=left></mtd>
                        </mtr>
                     </mtable>
                  </mrow>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>P</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-rel stretchy=false>|</mo>
            <mi>m</mi>
            <mo class=MathClass-rel stretchy=false>|</mo>
         </mrow>
      </msubsup>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   denotes the <span class=cmti-10>associated Legendre polynomial </span>of order
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   and degree
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>m</mi>
   </math>

   , and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>N</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-rel stretchy=false>|</mo>
            <mi>m</mi>
            <mo class=MathClass-rel stretchy=false>|</mo>
         </mrow>
      </msubsup>
   </math>
   is a normalization term, such as the Schmidt semi-normalization (SN3D) given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msubsup>
               <mrow>
                  <mi>N</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-rel stretchy=false>|</mo>
                  <mi>m</mi>
                  <mo class=MathClass-rel stretchy=false>|</mo>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel stretchy=false>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>2</mn>
                        <mo class=MathClass-bin stretchy=false>−</mo>
                        <msub>
                           <mrow>
                              <mi>δ</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <mn>4</mn>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
                  <mfrac>
                     <mrow>
                        <mo class=MathClass-open stretchy=false>(</mo>
                        <mi>n</mi>
                        <mo class=MathClass-bin stretchy=false>−</mo>
                        <mo class=MathClass-rel stretchy=false>|</mo>
                        <mi>m</mi>
                        <mo class=MathClass-rel stretchy=false>|</mo>
                        <mo class=MathClass-close stretchy=false>)</mo>
                        <mo class=MathClass-punc stretchy=false>!</mo>
                     </mrow>
                     <mrow>
                        <mo class=MathClass-open stretchy=false>(</mo>
                        <mi>n</mi>
                        <mo class=MathClass-bin stretchy=false>+</mo>
                        <mo class=MathClass-rel stretchy=false>|</mo>
                        <mi>m</mi>
                        <mo class=MathClass-rel stretchy=false>|</mo>
                        <mo class=MathClass-close stretchy=false>)</mo>
                        <mo class=MathClass-punc stretchy=false>!</mo>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mi> </mi>
            <mo class=MathClass-punc stretchy=false>.</mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   With increasing order
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
   </math>
   the spherical harmonics show increasingly complex directivity patterns, as depicted in <a href=#x1-1005r1>Figure 1</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/03/24/theory/figure-1.svg alt="Figure 1. Real-valued Spherical Harmonics up to <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>N</mi><mo class=MathClass-rel stretchy=false>=</mo><mn>2</mn></math>."><figcaption class=caption>Figure 1. Real-valued Spherical Harmonics up to <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>N</mi><mo class=MathClass-rel stretchy=false>=</mo><mn>2</mn></math>.</figcaption></figure>
<p>
   In their unbounded form (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
      <mo class=MathClass-rel stretchy=false>→</mo>
      <mi>∞</mi>
   </math>
   ) SHs constitute an <span class=cmti-10>orthonormal basis </span>for functions defined on the surface of the unit sphere. It is by this important property, that any sound field can be represented by a set of ambisonic (expansion) coefficients
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msubsup>
   </math>
   .<br class=newline>
<p>
   In practice, this spherical harmonic expansion is typically truncated to a finite number of coefficients with order
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>n</mi>
      <mo class=MathClass-rel stretchy=false>≤</mo>
      <mi>N</mi>
   </math>
   , yielding a total of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>O</mi>
      <mo class=MathClass-rel stretchy=false>=</mo>
      <msup>
         <mrow>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>N</mi>
            <mo class=MathClass-bin stretchy=false>+</mo>
            <mn>1</mn>
            <mo class=MathClass-close stretchy=false>)</mo>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   coefficient channels (i.e. Ambisonic channels) to be stored or transmitted. The Ambisonic sound field representation is therefore an approximation of the real sound field with limited spatial resolution.
<h4 class=likesubsectionHead>Ambisonic encoding</h4>
<p>
   The process of transforming a sound field into an Ambisonics coefficient representation (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msubsup>
   </math>
   ) is commonly denoted as <span class=cmti-10>Ambisonic encoding</span>. Real sound fields can be transformed (i.e. recorded) into an Ambisonic representation with the use of Ambisonic microphones, such as those depicted in <a href=#x1-2004r2>Figure 2</a>.
   <figure class=figure>
<div class=subfigure><p><img src=${config.ASSETS_URL}/03/24/theory/figure-2.png alt=PIC></figure>
<p>
   From <a href=#x1-1005r1>Figure 1</a> it can be seen, that the patterns of the SH functions up to order
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
      <mo class=MathClass-rel stretchy=false>=</mo>
      <mn>1</mn>
   </math>
   (referred to as First Order Ambisonics (FOA)) correspond with those of typical omnidirectional and figure-of-eight microphones (see Lesson 4.14 for more details), making such recording setups realizable with common studio equipment. In contrast, recordings with a higher order (i.e. Higher Order Ambisonics (HOA)) can only be achieved with (spherical) microphone arrays, which sample the spherical harmonics at multiple positions, and with additional signal processing steps to compensate, for example, the distortions introduced by the spherical array itself.<br class=newline>
<p>
   Additionally, virtual sound scenes can be encoded in the Ambisonics format, by multiplying any number
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>K</mi>
   </math>
   of (mono) source signals
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   with the SH functions
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mstyle mathvariant=bold>
         <mi mathvariant=normal>Ω</mi>
      </mstyle>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   at the desired source directions
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold>
         <msub>
            <mi mathvariant=normal>Ω</mi>
            <mn>s,k</mn>
         </msub>
      </mstyle>
      <mo class=MathClass-punc stretchy=false>:</mo>
      <mo class=MathClass-rel stretchy=false>=</mo>
      <msup>
         <mrow>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>𝜃</mi>
            <mo class=MathClass-punc stretchy=false>,</mo>
            <mi>φ</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msup>
   </math>
   as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msubsup>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel stretchy=false>=</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>k</mi>
                  <mo class=MathClass-rel stretchy=false>=</mo>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>K</mi>
               </mrow>
            </munderover>
            <msubsup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mstyle mathvariant=bold>
            <msub>
              <mi mathvariant=normal>Ω</mi>
              <mn>s,k</mn>
            </msub>
            </mstyle>

            <mo class=MathClass-close stretchy=false>)</mo>
            <mi> </mi>
            <msub>
               <mrow>
                  <mi>s</mi>
               </mrow>
               <mrow>
                  <mi>k</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi> </mi>
            <mo class=MathClass-punc stretchy=false>.</mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   To facilitate the exchange and reproduction of Ambisonic data, the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>O</mi>
      <mo class=MathClass-rel stretchy=false>=</mo>
      <msup>
         <mrow>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>N</mi>
            <mo class=MathClass-bin stretchy=false>+</mo>
            <mn>1</mn>
            <mo class=MathClass-close stretchy=false>)</mo>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   coefficient channels, obtained from recordings or by encoding virtual sources (e.g., sound generated with a synthesizer), need to be arranged and transmitted using a common format. The two most common file formats are the <span class=cmti-10>B-Format </span>with Furse-Malham (FuMa) [<span class=cmbx-10>malham1999</span>] weights and channel ordering (limited to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
      <mo class=MathClass-rel stretchy=false>=</mo>
      <mn>3</mn>
   </math>
   ), and the more recent AmbiX [<span class=cmbx-10>nachbar2011</span>] format which uses the previously introduced SN3D normalized spherical harmonics (see <a href=#x1-1004r2>Equation 2</a>) and a channel arrangement according to the Ambisonics Channel Numbering (ACN)
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>A</mi>
            <mi>C</mi>
            <mi>N</mi>
            <mo class=MathClass-rel stretchy=false>=</mo>
            <msup>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-bin stretchy=false>+</mo>
            <mi>n</mi>
            <mo class=MathClass-bin stretchy=false>+</mo>
            <mi>m</mi>
            <mi> </mi>
            <mo class=MathClass-punc stretchy=false>.</mo>
         </math>
      <td class=eq-no>(4)
</table>
<h4 class=likesubsectionHead>Ambisonic decoding</h4>
<p>Depending on the application, sound fields recorded or transmitted in the Ambisonics format can be reproduced over multi-channel loudspeaker arrangements or (binaurally) via headphones. The process of transforming Ambisonic streams back into corresponding channel representations is usually denoted as <span class=cmti-10>Ambisonic</span> <span class=cmti-10>decoding</span>.<br class=newline>
<p>
   The decoding of Ambisonic audio streams to loudspeakers is a topic which has been approached in different ways by various researchers [<span class=cmbx-10>zotter2019</span>]. Generally, the assumption is that the encoded sound field can be reconstructed by a superposition of plane waves arriving at the listening position (i.e. the center of the coordinate system) from
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
   </math>
   different loudspeakers with discrete positions
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant=normal>Ω</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc stretchy=false>:</mo>
      <mo class=MathClass-rel stretchy=false>=</mo>
      <mo class=MathClass-open stretchy=false>(</mo>
      <msub>
         <mrow>
            <mi>𝜃</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc stretchy=false>,</mo>
      <msub>
         <mrow>
            <mi>φ</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   placed (uniformly) around the listener. Examples of such uniform, ideal loudspeaker arrangements are depicted in <a href=#x1-3001r3>Figure 3</a>.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/03/24/theory/figure-3.svg alt="Figure 3. Example of various spherical t-Designs for optimal loudspeaker decoding [<span class=cmbx-10>zotter2012</span>]."><figcaption class=caption>Figure 3. Example of various spherical t-Designs for optimal loudspeaker decoding [<span class=cmbx-10>zotter2012</span>].</figcaption></figure>
<p>
   The loudspeaker signals
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
   </math>
   can be obtained from the encoded Ambisonics sound field
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msubsup>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   by applying a decoder with coefficients
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mo class=MathClass-punc stretchy=false>,</mo>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msubsup>
   </math>
   as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>x</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel stretchy=false>=</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel stretchy=false>=</mo>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </munderover>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mo class=MathClass-rel stretchy=false>=</mo>
                  <mo class=MathClass-bin stretchy=false>−</mo>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </munderover>
            <msubsup>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-punc stretchy=false>,</mo>
                  <mi>l</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msubsup>
            <mi> </mi>
            <msubsup>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi> </mi>
            <mo class=MathClass-punc stretchy=false>,</mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>which combined with the encoding given in <a href=#x1-2007r3>Equation 3</a> yields individual virtual sources
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>g</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel stretchy=false>=</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel stretchy=false>=</mo>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </munderover>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mo class=MathClass-rel stretchy=false>=</mo>
                  <mo class=MathClass-bin stretchy=false>−</mo>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </munderover>
            <msubsup>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-punc stretchy=false>,</mo>
                  <mi>l</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msubsup>
            <mi> </mi>
            <msubsup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
               </mrow>
            </msubsup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mstyle mathvariant=bold>
            <msub>
              <mi mathvariant=normal>Ω</mi>
              <mn>s</mn>
            </msub>
            </mstyle>

            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>written in vector notation as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>g</mi>
            <mo class=MathClass-rel stretchy=false>=</mo>
            <mi>D</mi>
            <mi> </mi>
            <msub>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mstyle mathvariant=bold>
                           <msub>
              <mi mathvariant=normal>Ω</mi>
              <mn>s</mn>
            </msub>
            </mstyle>

            <mo class=MathClass-close stretchy=false>)</mo>
            <mi> </mi>
            <mo class=MathClass-punc stretchy=false>.</mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   The main challenge in the decoding process is therefore to obtain an optimal set of decoding coefficients
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>d</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
            <mo class=MathClass-punc stretchy=false>,</mo>
            <mi>l</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
         </mrow>
      </msubsup>
   </math>
   or <span class=cmti-10>decoder matrix </span>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>D</mi>
   </math>
   . For uniform loudspeaker arrangements where the number of available loudspeakers
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
   </math>
   is equal to the number of Ambisonic coefficients
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>O</mi>
   </math>
   a <span class=cmti-10>sampler decoder </span>is often implemented as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>D</mi>
            <mo class=MathClass-rel stretchy=false>=</mo>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>4</mn>
                        <mi>π</mi>
                     </mrow>
                     <mrow>
                        <mi>L</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mi> </mi>
            <msup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </msup>
            <mi> </mi>
            <mo class=MathClass-punc stretchy=false>,</mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msqrt>
         <mrow>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </mfrac>
         </mrow>
      </msqrt>
   </math>
   is a normalization factor and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=bold>
                     <msub>
              <mi mathvariant=normal>Y</mi>
              <mn>N</mn>
            </msub>
      </mstyle>

      <mo class=MathClass-rel stretchy=false>=</mo>
      <mrow>
         <mo fence=true form=prefix>[</mo>
         <mrow>
            <msub>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mstyle mathvariant=bold>
               <msub>
                  <mrow>
                     <mi mathvariant=normal>Ω</mi>
                  </mrow>
                  <mrow>
                     <mn>1</mn>
                  </mrow>
               </msub>
            </mstyle>
            <mo class=MathClass-punc stretchy=false>,</mo>
            <mo class=MathClass-punc stretchy=false>.</mo>
            <mo class=MathClass-punc stretchy=false>.</mo>
            <mo class=MathClass-punc stretchy=false>.</mo>
            <mo class=MathClass-punc stretchy=false>,</mo>
            <msub>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mstyle mathvariant=bold>
               <msub>
                  <mrow>
                     <mi mathvariant=normal>Ω</mi>
                  </mrow>
                  <mrow>
                     <mi>L</mi>
                  </mrow>
               </msub>
            </mstyle>
         </mrow>
         <mo fence=true form=postfix>]</mo>
      </mrow>
   </math>
   are the spherical harmonics sampled at the loudspeaker directions
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi mathvariant=normal>Ω</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc stretchy=false>:</mo>
      <mo class=MathClass-rel stretchy=false>=</mo>
      <mo class=MathClass-open stretchy=false>(</mo>
      <msub>
         <mrow>
            <mi>𝜃</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-punc stretchy=false>,</mo>
      <msub>
         <mrow>
            <mi>φ</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   or alternatively the <span class=cmti-10>mode matching </span>pseudo-inverse is chosen with [<span class=cmbx-10>batke2010</span>]
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>D</mi>
            <mo class=MathClass-rel stretchy=false>=</mo>
            <msubsup>
               <mrow>
                  <mi>Y</mi>
               </mrow>
               <mrow>
                  <mi>N</mi>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </msubsup>
            <msup>
               <mrow>
                  <mo class=MathClass-open stretchy=false>(</mo>
                  <msub>
                     <mrow>
                        <mi>Y</mi>
                     </mrow>
                     <mrow>
                        <mi>N</mi>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>Y</mi>
                     </mrow>
                     <mrow>
                        <mi>N</mi>
                     </mrow>
                     <mrow>
                        <mi>T</mi>
                     </mrow>
                  </msubsup>
                  <mo class=MathClass-close stretchy=false>)</mo>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin stretchy=false>−</mo>
                  <mn>1</mn>
               </mrow>
            </msup>
            <mi> </mi>
            <mo class=MathClass-punc stretchy=false>.</mo>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   However, in most reproduction situations a uniform placement of the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
   </math>
   loudspeakers cannot be realized and Ambisonics will need to be decoded to an irregular loudspeaker setup. A good solution for this scenario is provided by the All-Round Ambisonic Decoding (AllRAD) [<span class=cmbx-10>zotter2012</span>] where the Ambisonic coefficient signals are first decoded to an optimal, virtual loudspeaker arrangement and the resulting signals are then panned to the real, non-ideal setup using Vector-Base Amplitude Panning (VBAP) (introduced in Lesson 3.21a), as visualized in <a href=#x1-3009r4>Figure 4</a>.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/03/24/theory/figure-4.svg alt="Figure 4. All-Round Ambisonic Decoding (AllRAD) introduced by Zotter and Frank [<span class=cmbx-10>zotter2012</span>]."><figcaption class=caption>Figure 4. All-Round Ambisonic Decoding (AllRAD)</a> introduced by Zotter and Frank [<span class=cmbx-10>zotter2012</span>].</figcaption></figure>
<p>
   Another interesting use case for Ambisonics encoded audio is binaural headphone reproduction, where the main advantage of using Ambisonics lies in the efficient implementation of spatial sound field manipulations, especially rotations around the three main axes
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>y</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>z</mi>
   </math>
   , denoted as <span class=cmti-10>yaw</span>, <span class=cmti-10>pitch </span>and <span class=cmti-10>roll </span>respectively. This makes the Ambisonics format especially useful for audiovisual applications such as 360&deg;

   video reproduction or Virtual Reality (VR).<br class=newline>
<p>
   The most straightforward approach to realize binaural headphone playback of Ambisonic audio streams is to first decode the Ambisonics input to a virtual loudspeaker arrangement consisting of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>L</mi>
   </math>
   loudspeakers (as introduced above) and then convolve each resulting loudspeaker channel signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>x</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   with a corresponding Head-Related Transfer Function (HRTF)
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mi>l</mi>
         </mrow>
         <mrow>
            <msup>
               <mrow>
                  <mi>l</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
            <mo class=MathClass-punc stretchy=false>,</mo>
            <msup>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mi>′</mi>
               </mrow>
            </msup>
         </mrow>
      </msubsup>
      <mo class=MathClass-open stretchy=false>(</mo>
      <mstyle mathvariant=bold>
         <msub>
            <mrow>
               <mi mathvariant=normal>Ω</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
      </mstyle>
      <mo class=MathClass-punc stretchy=false>,</mo>
      <mi>t</mi>
      <mo class=MathClass-close stretchy=false>)</mo>
   </math>
   (see Lesson 2.15) for that loudspeaker direction as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msup>
               <mrow>
                  <mi>x</mi>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-punc stretchy=false>,</mo>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
            </msup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel stretchy=false>=</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>l</mi>
                  <mo class=MathClass-rel stretchy=false>=</mo>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>L</mi>
               </mrow>
            </munderover>
            <msubsup>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
               <mrow>
                  <msup>
                     <mrow>
                        <mi>l</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
                  <mo class=MathClass-punc stretchy=false>,</mo>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>′</mi>
                     </mrow>
                  </msup>
               </mrow>
            </msubsup>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mstyle mathvariant=bold>

               <msub>
               <mi mathvariant=normal>Ω</mi>
               <mn>l</mn>
               </msub>
            </mstyle>

            <mo class=MathClass-punc stretchy=false>,</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi> </mi>
            <mo class=MathClass-bin stretchy=false>∗</mo>
            <mi> </mi>
            <msub>
               <mrow>
                  <mi>x</mi>
               </mrow>
               <mrow>
                  <mi>l</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mi> </mi>
            <mo class=MathClass-punc stretchy=false>.</mo>
         </math>
      <td class=eq-no>(10)
</table>
<h4 class=likesubsectionHead>Spatial audio applications</h4>
<p>By using Ambisonic microphones, complex sound scenes can be captured efficiently and reproduction can be scaled according to the available playback system (e.g., a loudspeaker array or headphones). Additionally, sound sources can be encoded into Ambisonics to generate purely virtual scenes (e.g., in computer games). Similarly, object-based or channel-based content can be encoded into Ambisonics (e.g., as virtual loudspeaker signals) and mixed (i.e. combined) with an existing Ambisonic audio stream [<span class=cmbx-10>herre2015</span>].<br class=newline>
<p>
   One major advantage of the Ambisonics format is that encoded sound scenes can be rotated using easily implementable rotation matrices and vector multiplication. For this reason, the format is especially relevant for multimodal, dynamic spatial audio applications, such as computer games, 360&deg;
   video, or VR, where listener is expected to move or rotate inside a virtual scene. Ambisonic encoders and decoders are available in various software and hardware solutions, sometimes with additional sound field manipulation capabilities and spatial effects, such as warping or directional enhancements [<span class=cmbx-10>iem2021</span>, <span class=cmbx-10>kronlachner2020</span>, <span class=cmbx-10>kruh2018</span>, <span class=cmbx-10>kronlachner2013</span>].<br class=newline>
<p>
<h3 class=likesectionHead>Glossary</h3>
<div class=supertabular>
<dl>
         <dt>ACN</dt>
         <dd>Ambisonics Channel Numbering.</dd>

         <dt>AllRAD</dt>
         <dd>All-Round Ambisonic Decoding.</dd>

         <dt>FOA</dt>
         <dd>First Order Ambisonics.</dd>

         <dt>FuMa</dt>
         <dd>Furse-Malham.</dd>

         <dt>HOA</dt>
         <dd>Higher Order Ambisonics.</dd>

         <dt>HRTF</dt>
         <dd>Head-Related Transfer Function.</dd>

         <dt>SH</dt>
         <dd>Spherical Harmonic.</dd>

         <dt>VBAP</dt>
         <dd>Vector-Base Amplitude Panning.</dd>

         <dt>VR</dt>
         <dd>Virtual Reality.</dd>

 </dl>
</div>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-klatzky class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Michael Gerzon. “Periphony: With-Height Sound Reproduction”. In: 21.1 (1973).
   <dt id=X0-potard class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Peter Fellgett. “Ambisonics. Part one: General system description”. en.
In: Studio Sound 17 (1975), p. 13
   <dt id=X0-rumsey class=thebibliography>[3]
   <dd id=bib-3 class=thebibliography>
      <p>Michael Gerzon. “Ambisonics. Part two: Studio techniques”. en. In: Stu-
dio Sound 17 (1975), p. 13.
   <dt id=X0-lossius class=thebibliography>[4]
   <dd id=bib-4 class=thebibliography>
      <p>Jens Ahrens. Analytic Methods of Sound Field Synthesis. en. Berlin,
Heidelberg: Springer Berlin Heidelberg, 2012. ISBN: 978-3-642-25742-
1 978-3-642-25743-8. DOI: 10.1007/978-3-642-25743-8. URL: http:
/ / link . springer . com / 10 . 1007 / 978 - 3 - 642 - 25743 - 8 (visited on
10/27/2021).
<dt id=X0-pulkki class=thebibliography>[5]
   <dd id=bib-5 class=thebibliography>
      <p>Christian Nachbar et al. “AMBIX - A SUGGESTED AMBISONICS FOR-
MAT”. en. In: (2011), p. 11.
   <dt id=X0-riedmiller class=thebibliography>[6]
   <dd id=bib-6 class=thebibliography>
      <p>D Malham. “Higher order Ambisonic systems for the spatialisation of
sound”. In: ICMC Proceedings 1999. 1999.
   <dt id=X0-immersive_sound class=thebibliography>[7]
   <dd id=bib-7 class=thebibliography>
      <p>Franz Zotter and Matthias Frank. Ambisonics: A Practical 3D Audio
Theory for Recording, Studio Production, Sound Reinforcement, and
Virtual Reality. en. Vol. 19. Springer Topics in Signal Processing. Cham:
Springer International Publishing, 2019. ISBN: 978-3-030-17206-0 978-
3-030-17207-7.DOI:10.1007/978-3-030-17207-7.URL:http:
//link.springer.com/10.1007/978-3-030-17207-7(visitedon
10/27/2021).
   <dt class=thebibliography>[8]
   <dd id=bib-8 class=thebibliography>
      <p>Franz Zotter and Matthias Frank. “All-Round Ambisonic Panning and
Decoding”. en. In: J. Audio Eng. Soc. 60.10 (2012), p. 14.
     <dt class=thebibliography>[9]
   <dd id=bib-9 class=thebibliography>
      <p>Johann-Markus Batke and Florian Keiler. “Investigation of Robust Pan-
ning Functions for 3D Loudspeaker Setups”. en. In: (2010), p. 9.
         <dt class=thebibliography>[10]
   <dd id=bib-10 class=thebibliography>
      <p>Jurgen Herre et al. “MPEG-H 3D Audio—The New Standard for Cod-
ing of Immersive Spatial Audio”. en. In: IEEE Journal of Selected Topics
in Signal Processing 9.5 (Aug. 2015), pp. 770–779. ISSN: 1932-4553,
1941-0484. DOI: 10 / gm8h2d. URL: http : / / ieeexplore . ieee . org /
document/7056445/ (visited on 10/27/2021).
         <dt class=thebibliography>[11]
   <dd id=bib-11 class=thebibliography>
      <p>IEM Plugin Suite. 2021. URL: https://plugins.iem.at/.
         <dt class=thebibliography>[12]
   <dd id=bib-12 class=thebibliography>
      <p>Matthias Kronlachner. AmbiX Suite. 2020. URL: https://github.com/
kronihias/ambix.
         <dt class=thebibliography>[13]
   <dd id=bib-13 class=thebibliography>
      <p> Andre Kruh-Elendt et al. “Tools for the production of spatial audio within
BINCI”. en. In: 2018, p. 3.
         <dt class=thebibliography>[14]
   <dd id=bib-14 class=thebibliography>
      <p>Matthias Kronlachner. “Ambisonics plug-in suite for production and
performance usage”. en. In: 2013, p. 5.
</dl>
`;

export default theory;
